import { useState, type FormEvent, type ComponentType } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { NvsLogo } from '../components/NvsLogo';
import { ColdChainIcon, ComplianceIcon, SecureIcon } from '../components/TrustIcons';

interface LocationState {
  from?: string;
}

const TRUST_POINTS: Array<{ Icon: ComponentType<{ className?: string }>; title: string; detail: string }> = [
  { Icon: ColdChainIcon, title: 'Cold Chain Logistics', detail: '2–8°C & 15–25°C controlled' },
  { Icon: ComplianceIcon, title: 'Regulatory Compliance', detail: 'GDP & MHRA approved' },
  { Icon: SecureIcon, title: 'Secure Handling', detail: 'Controlled drugs certified' },
];

export function Login() {
  const { user, sendMagicLink } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) {
    const from = (location.state as LocationState | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  // Surface a one-shot error if the user arrived here from a bad email link.
  const params = new URLSearchParams(location.search);
  const linkError = params.get('error');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await sendMagicLink(email.trim());
      // Always show the same confirmation, whether or not an account exists —
      // this prevents account enumeration.
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send sign-in link');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="nvs-login">
      {/* Brand / trust panel */}
      <aside className="nvs-login-hero">
        <span className="nvs-hero-circle nvs-hero-circle--tr" aria-hidden="true" />
        <span className="nvs-hero-circle nvs-hero-circle--bl" aria-hidden="true" />

        <div className="nvs-login-hero-inner">
          <div className="nvs-brand-lockup">
            <NvsLogo className="nvs-brand-logo" />
            <div className="nvs-wordmark">
              <span className="nvs-wordmark-name">NVS</span>
              <span className="nvs-wordmark-sub">Logistics</span>
            </div>
          </div>

          <div className="nvs-hero-copy">
            <span className="nvs-eyebrow nvs-eyebrow--hero">Reporting &amp; Analytics</span>
            <h1 className="nvs-hero-title">Delivery Reporting</h1>
            <p className="nvs-tagline">Temperature controlled supply chain and logistics</p>
          </div>

          <ul className="nvs-trust">
            {TRUST_POINTS.map(({ Icon, title, detail }) => (
              <li key={title} className="nvs-trust-item">
                <span className="nvs-trust-icon" aria-hidden="true">
                  <Icon className="nvs-trust-glyph" />
                </span>
                <span>
                  <strong>{title}</strong>
                  <span className="nvs-trust-detail">{detail}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="nvs-heritage">
            <span className="nvs-heritage-num">35+</span>
            <span className="nvs-heritage-text">Years of healthcare logistics excellence</span>
          </div>
        </div>
      </aside>

      {/* Sign-in panel */}
      <main className="nvs-login-main">
        <div className="nvs-login-card">
          {sent ? (
            <div className="nvs-sent">
              <span className="nvs-sent-icon" aria-hidden="true">✓</span>
              <h2>Check your inbox</h2>
              <p className="muted" role="status">
                If an account exists for <strong>{email}</strong>, we&rsquo;ve emailed a secure
                sign-in link. Click it to access the portal — no password needed.
              </p>
              <button
                type="button"
                className="nvs-link-btn"
                onClick={() => {
                  setSent(false);
                  setError(null);
                }}
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <div className="nvs-welcome">
                <h2>Welcome back</h2>
                <p className="muted">Sign in to view your delivery performance reporting</p>
              </div>

              {linkError === 'expired_link' && (
                <p className="form-error" role="alert">
                  That link has expired. Enter your email below to get a fresh one.
                </p>
              )}
              {linkError === 'invalid_link' && (
                <p className="form-error" role="alert">
                  That link wasn&rsquo;t valid. Enter your email below to get a fresh one.
                </p>
              )}

              <form onSubmit={onSubmit} noValidate>
                <label>
                  Work email address
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="you@nvsgroup.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                {error && <p className="form-error" role="alert">{error}</p>}
                <button type="submit" className="nvs-primary-btn" disabled={submitting}>
                  <svg className="nvs-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {submitting ? 'Sending secure link…' : 'Sign in to Portal'}
                </button>
              </form>

              <div className="nvs-divider">
                <span>Secure authentication via Locate2u</span>
              </div>

              <p className="nvs-terms muted">
                By signing in, you agree to NVS Logistics&rsquo;{' '}
                <a href="#terms">Terms of Use</a> &amp; <a href="#privacy">Privacy Policy</a>
              </p>
            </>
          )}
        </div>

        <p className="nvs-login-addr muted">
          NVS Logistics · Spitfire House, Stoke-on-Trent, ST1 5FG
        </p>
      </main>
    </div>
  );
}
